import React from 'react';
import { Box, Typography, IconButton, Button, Dialog, DialogContent, Checkbox, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';

const formatPrice = (price) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(price);

/**
 * WorldCupProductModal - Colombia theme product detail modal.
 * Design inspired by: dark card with food photo, flag brushstrokes in corners,
 * "RECOMENDADA" badge, impact typography, yellow add button.
 */
export const WorldCupProductModal = ({
    open, onClose, product, theme,
    quantity, setQuantity,
    selectedVariants, setSelectedVariants,
    onAddToCart
}) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [zoomImage, setZoomImage] = React.useState(null);

    React.useEffect(() => {
        setIsExpanded(false);
        setZoomImage(null);
    }, [product]);

    if (!product) return null;

    const hasDiscount = !!(product.discounted_price && Number(product.discounted_price) > 0 && Number(product.discounted_price) < Number(product.price));
    const basePrice = hasDiscount ? product.discounted_price : product.price;

    let variantsExtraPrice = 0;
    if (product.is_complex && product.detail_variants) {
        Object.keys(selectedVariants).forEach(key => {
            const qty = selectedVariants[key];
            if (qty > 0) {
                const [gIdx, oIdx] = key.split('_');
                const option = product.detail_variants[gIdx]?.details[oIdx];
                if (option && option.value) {
                    variantsExtraPrice += Number(option.value) * qty;
                }
            }
        });
    }

    const finalPrice = Number(basePrice) + variantsExtraPrice;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        backgroundColor: '#0D0D0D',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        border: `1px solid rgba(252,209,22,0.3)`,
                        boxShadow: `0 0 60px rgba(0,0,0,0.9), 0 0 0 1px ${theme.yellow}40`,
                        fontFamily: "'Outfit','Roboto',sans-serif",
                        position: 'relative',
                    }
                }}
            >
                {/* === FLAG BRUSH TOP-LEFT === */}
                <Box component="img"
                    src={theme.brushTopLeft}
                    alt=""
                    sx={{
                        position: 'absolute', top: -20, left: -20,
                        width: '160px', opacity: 0.75,
                        transform: 'scaleX(-1) scaleY(-1)',
                        zIndex: 10, pointerEvents: 'none',
                        filter: 'contrast(1.2)',
                    }}
                />

                {/* === FLAG BRUSH BOTTOM-RIGHT === */}
                <Box component="img"
                    src={theme.brushTopLeft}
                    alt=""
                    sx={{
                        position: 'absolute', bottom: -20, right: -20,
                        width: '160px', opacity: 0.75,
                        zIndex: 0, pointerEvents: 'none',
                        filter: 'contrast(1.2)',
                    }}
                />

                {/* === SOCCER BALL WATERMARK === */}
                <Box sx={{
                    position: 'absolute', right: -30, top: '40%',
                    opacity: 0.04, zIndex: 1, pointerEvents: 'none',
                }}>
                    <SportsSoccerIcon sx={{ fontSize: 260, color: '#FFFFFF' }} />
                </Box>

                {/* === CLOSE BUTTON === */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute', top: 14, right: 14,
                        color: theme.yellow,
                        border: `2px solid ${theme.yellow}`,
                        zIndex: 20, bgcolor: 'rgba(0,0,0,0.7)',
                        width: 34, height: 34,
                        '&:hover': { bgcolor: theme.yellow, color: '#000' },
                        transition: 'all 0.2s',
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', zIndex: 2, position: 'relative' }}>

                    {/* === PRODUCT IMAGE === */}
                    <Box
                        onClick={() => product.image_url && setZoomImage(product.image_url)}
                        sx={{
                            position: 'relative',
                            width: '100%',
                            height: '260px',
                            overflow: 'hidden',
                            cursor: product.image_url ? 'pointer' : 'default'
                        }}
                    >
                        {product.image_url ? (
                            <Box
                                component="img"
                                src={product.image_url}
                                alt={product.name}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <Box sx={{
                                width: '100%', height: '100%',
                                background: `linear-gradient(135deg, ${theme.blue}33, ${theme.blue}66)`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <RestaurantMenuIcon sx={{ fontSize: 72, color: theme.yellow, opacity: 0.5 }} />
                            </Box>
                        )}
                        {/* Gradient fade bottom */}
                        <Box sx={{
                            position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%',
                            background: 'linear-gradient(to bottom, transparent, #0D0D0D)',
                        }} />
                    </Box>

                    {/* === INFO BLOCK === */}
                    <Box sx={{ px: 2.5, pb: 2.5, pt: 0, position: 'relative', zIndex: 5 }}>

                        {/* Product name */}
                        <Typography sx={{
                            fontWeight: 900,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            color: '#FFFFFF',
                            textTransform: 'uppercase',
                            fontFamily: "'Impact','Outfit',sans-serif",
                            letterSpacing: '2px',
                            lineHeight: 1,
                            mb: 1,
                            textShadow: `0px 2px 8px rgba(0,0,0,0.8)`,
                        }}>
                            {product.name}
                        </Typography>

                        {/* Prices */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <Typography sx={{
                                fontWeight: 900, fontSize: '1.6rem',
                                color: theme.yellow, letterSpacing: '1px',
                            }}>
                                {formatPrice(finalPrice)}
                            </Typography>
                            {hasDiscount && (
                                <Typography sx={{
                                    fontWeight: 700, fontSize: '1.1rem',
                                    color: '#888',
                                    textDecoration: 'line-through',
                                    textDecorationColor: theme.red,
                                    textDecorationThickness: '2px',
                                }}>
                                    {formatPrice(product.price)}
                                </Typography>
                            )}
                        </Box>

                        {/* Divider */}
                        <Box sx={{
                            height: '2px',
                            background: `linear-gradient(90deg, ${theme.yellow}, ${theme.blue}, ${theme.red})`,
                            mb: 1.5, borderRadius: '2px',
                        }} />

                        {/* Description */}
                        {product.description && (() => {
                            const shouldTruncate = product.description.length > 150;
                            const displayText = isExpanded
                                ? product.description
                                : shouldTruncate
                                    ? `${product.description.substring(0, 150)}...`
                                    : product.description;
                            return (
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography sx={{ color: '#CCCCCC', fontSize: '0.95rem', lineHeight: 1.5 }}>
                                        {displayText}{' '}
                                        {shouldTruncate && (
                                            <span
                                                onClick={() => setIsExpanded(!isExpanded)}
                                                style={{
                                                    color: theme.yellow,
                                                    cursor: 'pointer',
                                                    fontWeight: 800,
                                                    marginLeft: '4px',
                                                    textDecoration: 'underline'
                                                }}
                                            >
                                                {isExpanded ? 'Ver menos' : 'Ver más'}
                                            </span>
                                        )}
                                    </Typography>
                                </Box>
                            );
                        })()}

                        {/* Variantes / Complementos */}
                        {product.is_complex && product.detail_variants && (
                            <Box sx={{ mb: 2.5 }}>
                                {product.detail_variants.map((group, groupIndex) => (
                                    <Box key={groupIndex} sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                            <Box sx={{ width: 4, height: 18, borderRadius: 2, bgcolor: theme.yellow }} />
                                            <Typography variant="subtitle2" sx={{
                                                fontWeight: 800, fontSize: '0.95rem',
                                                color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.5px',
                                            }}>
                                                {group.option_display_name}
                                            </Typography>
                                        </Box>
                                        <Grid container spacing={1}>
                                            {group.details.map((option, optionIndex) => {
                                                const key = `${groupIndex}_${optionIndex}`;
                                                const isSelected = (selectedVariants[key] || 0) > 0;
                                                const hasPrice = Number(option.value || 0) > 0;
                                                return (
                                                    <Grid item xs={12} sm={6} key={optionIndex}>
                                                        <Box 
                                                            onClick={() => setSelectedVariants(prev => ({ ...prev, [key]: isSelected ? 0 : 1 }))}
                                                            sx={{
                                                                border: `2px solid ${isSelected ? theme.yellow : 'rgba(255,255,255,0.1)'}`,
                                                                borderRadius: '12px',
                                                                backgroundColor: isSelected ? 'rgba(252,209,22,0.1)' : 'rgba(255,255,255,0.03)',
                                                                transition: 'all 0.2s',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                                p: 1.5, cursor: 'pointer', height: '100%',
                                                                '&:hover': {
                                                                    backgroundColor: isSelected ? 'rgba(252,209,22,0.15)' : 'rgba(255,255,255,0.08)'
                                                                }
                                                            }}
                                                        >
                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#FFF' }}>
                                                                    {option.name}
                                                                </Typography>
                                                                {hasPrice && (
                                                                    <Typography sx={{ fontWeight: 800, color: theme.yellow, fontSize: '0.8rem', mt: 0.3 }}>
                                                                        +{formatPrice(option.value)}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Checkbox 
                                                                checked={isSelected}
                                                                disableRipple
                                                                sx={{
                                                                    color: 'rgba(255,255,255,0.3)',
                                                                    p: 0,
                                                                    '&.Mui-checked': {
                                                                        color: theme.yellow,
                                                                    },
                                                                }}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Controls */}
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                            {/* Quantity */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                border: `2px solid ${theme.yellow}`,
                                borderRadius: '24px', px: 0.5, py: 0.25,
                                bgcolor: 'rgba(0,0,0,0.5)',
                            }}>
                                <IconButton size="small" onClick={() => setQuantity && setQuantity(Math.max(1, (quantity || 1) - 1))} sx={{ color: theme.yellow }}>
                                    <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Typography sx={{ color: '#FFF', fontWeight: 900, fontSize: '1.1rem', minWidth: '24px', textAlign: 'center' }}>
                                    {quantity || 1}
                                </Typography>
                                <IconButton size="small" onClick={() => setQuantity && setQuantity((quantity || 1) + 1)} sx={{ color: theme.yellow }}>
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            {/* Add to cart */}
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => onAddToCart && onAddToCart(product)}
                                startIcon={<ShoppingCartIcon />}
                                sx={{
                                    bgcolor: theme.yellow,
                                    color: '#000000',
                                    fontWeight: 900,
                                    fontSize: '0.95rem',
                                    py: 1.4,
                                    borderRadius: '24px',
                                    boxShadow: `0 8px 24px rgba(252,209,22,0.3)`,
                                    '&:hover': { bgcolor: '#FFD700', transform: 'translateY(-2px)' },
                                    transition: 'all 0.2s',
                                }}
                            >
                                AGREGAR • {formatPrice(finalPrice * (quantity || 1))}
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Zoom Image Dialog */}
            <Dialog open={!!zoomImage} onClose={() => setZoomImage(null)} maxWidth="md" PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'hidden' } }}>
                <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'rgba(0,0,0,0.9)', p: 1, borderRadius: '16px', border: `2px solid ${theme.yellow}` }}>
                    <img src={zoomImage} style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', borderRadius: '12px' }} alt="Zoom" />
                    <IconButton
                        onClick={() => setZoomImage(null)}
                        sx={{ position: 'absolute', right: 15, top: 15, color: theme.yellow, bgcolor: 'rgba(0,0,0,0.7)', border: `1px solid ${theme.yellow}`, '&:hover': { bgcolor: theme.yellow, color: '#000' } }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Dialog>
        </>
    );
};
